<template>
    <div :key="loadComponent">
        <div class="row">
            <div class="col" v-if="backgroundLoaded">
                <button v-if="!hasProjection" class="btn btn-default m-1" @click.prevent="addPicker">
                    Add Projection
                </button>
                <button v-if="hasProjection" class="btn btn-default m-1" @click.prevent="cancelPicker">
                    Remove Projection
                </button>
            </div>
        </div>
        <div class="row">
            <div class="col">
                <div class="responsive-canvas" ref="container">
                    <img v-if="background" :src="background" alt="Preview Media" @load="imgLoaded" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// import * as d3 from "d3";
import arrayGet from 'lodash/get';
import numeric from './../numeric/numeric-solve.min';

export default {
    props: {
        background: {
            required: true,
        },
        value: {
            required: true,
        },
    },
    data() {
        return {
            canvas: {
                height: null,
                width: null,
                transform: null,
                targetPoints: [],
                sourcePoints: [],
            },

            /** objects **/
            handle: null,
            anchors: null,
            transformer: null,

            /** booleans **/
            hasProjection: false,
            componentLoaded: true,
            backgroundLoaded: false,
        };
    },
    created() {
        this.hasProjection = arrayGet(this.value, 'points', false);
    },
    watch: {
        background: {
            deep: true,
            handler() {
                this.reloadComponent();
            },
        },
    },
    methods: {
        cancelPicker() {
            this.hasProjection = false;
            this.canvas = {
                height: null,
                width: null,
                transform: null,
                targetPoints: null,
                sourcePoints: null,
            };
            this.emitValue();
            this.reloadComponent();
        },
        addPicker() {
            this.hasProjection = true;
            this.createPicker();
        },
        emitValue() {
            this.$emit('input', {
                points: this.canvas.targetPoints,
                height: this.canvas.height,
                width: this.canvas.width,
            });
        },
        imgLoaded() {
            this.canvas.width = this.$refs.container.offsetWidth;
            this.canvas.height = this.$refs.container.offsetHeight;
            this.canvas.sourcePoints = [
                [0, 0],
                [this.canvas.width, 0],
                [this.canvas.width, this.canvas.height],
                [0, this.canvas.height],
            ];
            this.canvas.transform =
                ['', '-webkit-', '-moz-', '-ms-', '-o-'].reduce(function (p, v) {
                    return v + 'transform' in document.body.style ? v : p;
                }) + 'transform';
            this.backgroundLoaded = true;

            if (this.hasProjection) {
                this.createPicker();
            }
        },
        computeTargetPoints() {
            const _this = this;
            const points = arrayGet(this.value, 'points');
            if (points) {
                this.canvas.targetPoints = points.map(function (point) {
                    return [(point[0] / _this.value.width) * _this.canvas.width, (point[1] / _this.value.height) * _this.canvas.height];
                });
            } else {
                const top = this.canvas.height * 0.1;
                const bottom = this.canvas.height * 0.9;
                const left = this.canvas.width * 0.1;
                const right = this.canvas.width * 0.9;
                this.canvas.targetPoints = arrayGet(this.value, 'points')
                    ? arrayGet(this.value, 'points')
                    : [
                          [left, top],
                          [right, top],
                          [right, bottom],
                          [left, bottom],
                      ];
            }
        },
        createPicker() {
            const _this = this;
            // this.computePickerPoints();
            this.computeTargetPoints();
            window.addEventListener('resize', function () {
                _this.anchors.select('g').selectAll('.handle').remove();
                _this.canvas.width = _this.$refs.container.offsetWidth;
                _this.canvas.height = _this.$refs.container.offsetHeight;
                _this.computeTargetPoints();
                _this.transformed();
                _this.anchors.attr('width', _this.canvas.width).attr('height', _this.canvas.height);
                _this.drawHandles();
            });

            this.injectShape();

            // this.transformer.select("g").append("image")
            //     .attr("width", this.canvas.width)
            //     .attr("height", this.canvas.height);
            //
            // this.transformer.select("g")
            //     .append("div")
            //     // .attr("xlink:href", '')
            //     .attr("width", this.canvas.width)
            //     .attr("height", this.canvas.height)
            //     .attr("class", "foreground-image");

            this.transformer
                .select('g')
                .selectAll('.line--x')
                .data(d3.range(0, this.canvas.width + 1, 10))
                .enter()
                .append('line')
                .attr('class', 'line line--x')
                .attr('x1', function (d) {
                    return d;
                })
                .attr('x2', function (d) {
                    return d;
                })
                .attr('y1', 0)
                .attr('y2', this.canvas.height);

            this.transformer
                .select('g')
                .selectAll('.line--y')
                .data(d3.range(0, this.canvas.height + 1, 10))
                .enter()
                .append('line')
                .attr('class', 'line line--y')
                .attr('x1', 0)
                .attr('x2', this.canvas.width)
                .attr('y1', function (d) {
                    return d;
                })
                .attr('y2', function (d) {
                    return d;
                });

            this.drawHandles();
            this.transformed();
        },
        drawHandles() {
            const _this = this;
            this.handle = this.anchors
                .select('g')
                .selectAll('.handle')
                .data(this.canvas.targetPoints)
                .enter()
                .append('circle')
                .attr('class', 'handle')
                .attr('transform', function (d) {
                    return 'translate(' + d + ')';
                })
                .attr('r', 20)
                .call(
                    d3
                        .drag()
                        .subject(function (d) {
                            return { x: d[0], y: d[1] };
                        })
                        .on('drag', function (d) {
                            d3.select(this).attr('transform', 'translate(' + (d[0] = d3.event.x) + ',' + (d[1] = d3.event.y) + ')');
                            _this.transformed();
                        }),
                );
        },
        injectShape() {
            d3.select('.responsive-canvas')
                .selectAll('svg')
                .data(['transform', 'flat'])
                .enter()
                .append('svg')
                .attr('id', function (d) {
                    return d;
                })
                .attr('width', this.canvas.width)
                .attr('height', this.canvas.height)
                .append('g')
                .attr('transform', 'translate(0,0)');

            this.transformer = d3.select('#transform').style(this.canvas.transform + '-origin', '0px 0px 0');
            this.anchors = d3.select('#flat');
        },
        transformed() {
            for (var a = [], b = [], i = 0, n = this.canvas.sourcePoints.length; i < n; ++i) {
                let s = this.canvas.sourcePoints[i];
                let t = this.canvas.targetPoints[i];
                a.push([s[0], s[1], 1, 0, 0, 0, -s[0] * t[0], -s[1] * t[0]]), b.push(t[0]);
                a.push([0, 0, 0, s[0], s[1], 1, -s[0] * t[1], -s[1] * t[1]]), b.push(t[1]);
            }

            let X = numeric.solve(a, b, true);
            let matrix = [X[0], X[3], 0, X[6], X[1], X[4], 0, X[7], 0, 0, 1, 0, X[2], X[5], 0, 1].map(x => Math.round(x * 1000000) / 1000000);

            this.transformer.style('transform', 'matrix3d(' + matrix + ')');
            this.emitValue();
        },
    },
};
</script>

<style>
.responsive-canvas {
    position: relative;
    overflow: hidden;
}

.responsive-canvas img {
    width: 100%;
    max-width: 100%;
}

.responsive-canvas svg {
    position: absolute;
    top: 0;
    left: 0;
}

.responsive-canvas .handle {
    fill: none;
    pointer-events: all;
    stroke: transparent;
    stroke-width: 2px;
    cursor: move;
}

.responsive-canvas:hover .handle {
    stroke: #fff;
}

#transform {
    background: #ababab;
    /*background: #ababab url('/img/logos/logo-c-sm.svg') no-repeat center center;*/
    -webkit-background-size: contain;
    -moz-background-size: contain;
    -o-background-size: contain;
    background-size: contain;
}
</style>
